import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  PencilSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  UserIcon,
  ComputerDesktopIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  ClockIcon,
  TagIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { useSnackbar } from "../../context/SnackbarContext";
import { INCIDENT_STATUS_OPTIONS, INCIDENT_CATEGORIES, INCIDENT_CATEGORIES_WITH_OPTIONS, SUBTYPE_TO_CATEGORY } from "../../constants/AppConstants";

// Maps category ID → display name (e.g. "physical_security" → "Physical Security")
const CATEGORY_DISPLAY = Object.fromEntries(
  INCIDENT_CATEGORIES_WITH_OPTIONS.map(c => [c.id, c.name])
);
import { DataTable } from "../common/DataTable";
import EditIncidentModal from "./EditIncidentModal";

// Helper function to get category ID from incident subtype
const getCategoryIdFromSubtype = (subtype) => {
  if (!subtype) return null;
  return SUBTYPE_TO_CATEGORY[subtype.toLowerCase()] || null;
};

const AllIncidentsComponent = ({
  loading,
  incidents,
  incidentFilters,
  setIncidentFilters,
  pagination,
  setPagination,
  categories,
  loadIncidentDetails,
  selectedIncident,
  showIncidentModal,
  setShowIncidentModal,
  setSelectedIncident,
  getSeverityColor,
  getStatusColor,
  formatDate,
  updateIncidentStatus,
  incidentLogs,
  loadingLogs,
}) => {
  const [editStatus, setEditStatus] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  // Employee Name has no backend filter param (employee_name is resolved
  // from employee_id -> EmployeeMaster at render time, not a queryable
  // field on EmployeeIncidentReport) - filtered client-side on whatever
  // page is currently loaded, unlike the other (backend-driven) columns.
  const [employeeNameFilter, setEmployeeNameFilter] = useState("");
  const { showSnackbar } = useSnackbar();

  const toggleExpand = (id) =>
    setExpandedRowId((prev) => (prev === id ? null : id));

  // API handles all filtering and pagination server-side
  // incidents array is pre-filtered from parent component

  const handleOpenEdit = (incident) => {
    loadIncidentDetails(incident.id);
    setEditStatus("");
    setEditNotes("");
  };

  const handleSaveStatus = async () => {
    if (!editStatus || editStatus === selectedIncident?.status) {
      return;
    }
    setSavingStatus(true);
    try {
      await updateIncidentStatus(selectedIncident.id, editStatus, editNotes);
      setEditStatus("");
      setEditNotes("");
      showSnackbar("Incident status updated successfully", "success");
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || "Failed to update incident status";
      showSnackbar(message, "error");
    } finally {
      setSavingStatus(false);
    }
  };

  // Column configuration - meta.filter drives the DataTable header filter
  // for that column, same pattern as Risk Analysis/Asset Inventory. The
  // expanded detail panel (description/resolution/attachments/meta) is
  // rendered inside the Actions cell, not as a separate full-width
  // sub-row - common/DataTable's body renders exactly one <tr> per row.
  const columns = [
    {
      id: "incident_no",
      accessorFn: (incident) => incident.incident_no,
      header: "Incident No",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Incident No", placeholder: "e.g. INV00015" } },
      cell: ({ row }) => (
        <span className="font-medium text-gray-900 dark:text-white">{row.original.incident_no}</span>
      ),
    },
    {
      id: "employee_info",
      header: "Employee Name",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Employee Name", placeholder: "Filter employee name" } },
      cell: ({ row }) => {
        const info = row.original.employee_info;
        let display = 'Unknown';
        if (info?.employee_name) {
          display = info.employee_name;
          if (info.designation && info.designation !== 'N/A') display += `, ${info.designation}`;
          if (info.department && info.department !== 'N/A') display += `, ${info.department}`;
        }
        return <span className="text-gray-900 dark:text-gray-100">{display}</span>;
      },
    },
    {
      id: "title",
      accessorFn: (incident) => incident.title,
      header: "Title",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Title", placeholder: "Filter title" } },
      cell: ({ row }) => <span className="text-gray-900 dark:text-gray-100">{row.original.title}</span>,
    },
    {
      id: "category",
      accessorFn: (incident) => incident.incident_type,
      header: "Category",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Category", placeholder: "All categories", options: INCIDENT_CATEGORIES } },
      cell: ({ row }) => {
        const incident = row.original;
        const catName = CATEGORY_DISPLAY[incident.incident_type];
        const subtype = incident.affected_systems || incident.incident_type || '';
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-gray-700 dark:text-gray-300 text-xs">{subtype.replace(/_/g, ' ')}</span>
            {catName && <span className="text-[10px] text-blue-500 dark:text-blue-400 capitalize">{catName}</span>}
          </div>
        );
      },
    },
    {
      id: "severity",
      accessorFn: (incident) => incident.severity,
      header: "Severity",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          placeholder: "All severities",
          options: [
            { value: "critical", label: "Critical" },
            { value: "high", label: "High" },
            { value: "medium", label: "Medium" },
            { value: "low", label: "Low" },
          ],
        },
      },
      cell: ({ row }) => (
        <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full border ${getSeverityColor(row.original.severity)}`}>
          {(row.original.severity || '')?.toUpperCase()}
        </span>
      ),
    },
    {
      id: "status",
      accessorFn: (incident) => incident.status,
      header: "Status",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Status", placeholder: "All statuses", options: INCIDENT_STATUS_OPTIONS } },
      cell: ({ row }) => (
        <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full border ${getStatusColor(row.original.status)}`}>
          {(row.original.status || '')?.replace(/_/g, ' ').toUpperCase()}
        </span>
      ),
    },
    {
      id: "reported_date",
      accessorFn: (incident) => incident.reported_date || incident.created_at,
      header: "Created",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-gray-500 dark:text-gray-400">
          {formatDate(row.original.reported_date || row.original.created_at)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      size: 48,
      cell: ({ row }) => {
        const incident = row.original;
        const isExpanded = expandedRowId === incident.id;
        return (
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleOpenEdit(incident); }}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 flex items-center gap-1"
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Edit</span>
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); toggleExpand(incident.id); }}>
                {isExpanded
                  ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" />
                  : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />}
              </button>
            </div>
            {isExpanded && <IncidentDetailPanel incident={incident} getSeverityColor={getSeverityColor} getStatusColor={getStatusColor} formatDate={formatDate} />}
          </div>
        );
      },
    },
  ];

  // "title" maps to the backend's generic `search` param (title/description/
  // incident_no), "employee_info" is local-only (see employeeNameFilter).
  const columnFilters = [
    ...Object.entries(incidentFilters || {})
      .filter(([id, value]) => value && id !== 'search')
      .map(([id, value]) => ({ id, value })),
    ...(incidentFilters?.search ? [{ id: 'title', value: incidentFilters.search }] : []),
    ...(employeeNameFilter ? [{ id: 'employee_info', value: employeeNameFilter }] : []),
  ];

  const displayedIncidents = employeeNameFilter
    ? incidents.filter((incident) => {
        const name = incident.employee_info?.employee_name || '';
        return name.toLowerCase().includes(employeeNameFilter.toLowerCase());
      })
    : incidents;

  return (
    <motion.div
      key="incidents"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Data Table */}
      <DataTable
        columns={columns}
        data={displayedIncidents}
        getRowId={(incident) => String(incident.id)}
        loading={loading}
        emptyMessage="No incidents found"
        manualPagination
        rowCount={pagination?.total_count || 0}
        pagination={{
          pageIndex: (pagination?.page || 1) - 1,
          pageSize: pagination?.page_size || 10,
        }}
        onPaginationChange={(updater) => {
          const currentPagination = {
            pageIndex: (pagination?.page || 1) - 1,
            pageSize: pagination?.page_size || 10,
          };
          const nextPagination = typeof updater === 'function' ? updater(currentPagination) : updater;
          setPagination((prev) => ({ ...prev, page: nextPagination.pageIndex + 1, page_size: nextPagination.pageSize }));
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        manualFiltering
        columnFilters={columnFilters}
        onColumnFiltersChange={(updater) => {
          const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
          const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
          setIncidentFilters({
            incident_no: values.incident_no || '',
            category: values.category || '',
            severity: values.severity || '',
            status: values.status || '',
            search: values.title || '',
          });
          setEmployeeNameFilter(values.employee_info || '');
          setPagination((prev) => ({ ...prev, page: 1 }));
        }}
        className="overflow-hidden rounded-xl shadow-sm"
        maxBodyHeight="65vh"
      />

      {/* Edit Incident Modal */}
      <EditIncidentModal
        show={showIncidentModal}
        selectedIncident={selectedIncident}
        onClose={() => {setShowIncidentModal(false); setSelectedIncident(null);}}
        editStatus={editStatus}
        setEditStatus={setEditStatus}
        editNotes={editNotes}
        setEditNotes={setEditNotes}
        onSaveStatus={handleSaveStatus}
        savingStatus={savingStatus}
        getSeverityColor={getSeverityColor}
        getStatusColor={getStatusColor}
        formatDate={formatDate}
        incidentLogs={incidentLogs}
        loadingLogs={loadingLogs}
      />
    </motion.div>
  );
};

export default AllIncidentsComponent;

const MetaField = ({ icon, label, value, capitalize }) => (
  <div>
    <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-0.5">
      {icon}
      {label}
    </p>
    <p className={`text-xs font-medium text-gray-800 dark:text-gray-200 leading-snug ${capitalize ? 'capitalize' : ''}`}>
      {value || <span className="text-gray-400 dark:text-gray-600 italic">—</span>}
    </p>
  </div>
);

// Expanded incident detail, rendered inside the Actions column's own
// cell (not a connected full-width sub-row) - common/DataTable's body
// renders exactly one <tr> per row with no support for an extra row.
const IncidentDetailPanel = ({ incident, getSeverityColor, getStatusColor, formatDate }) => (
  <div className="mt-3 w-[32rem] bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-3">
      <div className="h-4 w-1 rounded-full bg-blue-500" />
      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
        Incident Details — {incident.incident_no}
      </span>
    </div>

    <div className="grid grid-cols-1 gap-3">
      {/* Description */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">
          <DocumentTextIcon className="w-3.5 h-3.5" />
          Description
        </p>
        {incident.description ? (
          <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
            {incident.description}
          </p>
        ) : (
          <p className="text-xs italic text-gray-400 dark:text-gray-500">No description provided.</p>
        )}

        {incident.resolution_notes && (
          <>
            <div className="my-3 border-t border-dashed border-gray-200 dark:border-gray-700" />
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">
              <ClipboardDocumentCheckIcon className="w-3.5 h-3.5" />
              Resolution Notes
            </p>
            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
              {incident.resolution_notes}
            </p>
          </>
        )}

        {incident.attachments && incident.attachments.length > 0 && (
          <>
            <div className="my-3 border-t border-dashed border-gray-200 dark:border-gray-700" />
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">
              <PaperClipIcon className="w-3.5 h-3.5" />
              Attachments ({incident.attachments.length})
            </p>
            <ul className="space-y-1.5">
              {incident.attachments.map((url, i) => {
                const filename = url.split('/').pop().split('?')[0];
                const ext = filename.split('.').pop().toLowerCase();
                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
                const isPdf = ext === 'pdf';
                const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
                const viewUrl = isOffice
                  ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
                  : url;
                return (
                  <li key={i}>
                    <a
                      href={viewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group"
                    >
                      <PaperClipIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                      <span className="text-xs text-blue-600 dark:text-blue-400 truncate group-hover:underline flex-1">
                        {filename}
                      </span>
                      <span className="text-[10px] uppercase font-semibold text-gray-400 flex-shrink-0">
                        {isOffice ? 'view' : isPdf ? 'pdf' : isImage ? 'image' : ext}
                      </span>
                      {isImage && (
                        <img
                          src={url}
                          alt={filename}
                          className="h-8 w-8 object-cover rounded border border-gray-200 dark:border-gray-600 flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Meta card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm space-y-3">
        <MetaField icon={<UserIcon className="w-3.5 h-3.5" />} label="Reporter"
          value={incident.reporter || incident.employee_info?.employee_name} />

        <MetaField icon={<TagIcon className="w-3.5 h-3.5" />} label="Category"
          value={
            CATEGORY_DISPLAY[incident.incident_type]
              ? `${CATEGORY_DISPLAY[incident.incident_type]}${incident.affected_systems ? ' — ' + incident.affected_systems : ''}`
              : (incident.incident_type || '').replace(/_/g, ' ')
          } capitalize />

        {incident.affected_systems && (
          <MetaField icon={<ComputerDesktopIcon className="w-3.5 h-3.5" />} label="Affected Systems"
            value={incident.affected_systems} />
        )}

        <MetaField icon={<CalendarDaysIcon className="w-3.5 h-3.5" />} label="Reported On"
          value={formatDate(incident.reported_date || incident.created_at)} />

        {incident.updated_at && (
          <MetaField icon={<ClockIcon className="w-3.5 h-3.5" />} label="Last Updated"
            value={formatDate(incident.updated_at)} />
        )}

        <div className="pt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${getSeverityColor(incident.severity)}`}>
            {incident.severity}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border ${getStatusColor(incident.status)}`}>
            {(incident.status || '').replace(/_/g, ' ')}
          </span>
        </div>
      </div>
    </div>
  </div>
);
